import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Search, ChevronDown, ChevronUp, X, AlertTriangle } from 'lucide-react';
import { DashboardLayout } from '../components/layouts/DashboardLayout';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '@/lib/supabaseClient';
import { toast } from 'sonner';

type Position = {
  id: string;
  type: 'LONG' | 'SHORT';
  symbol: string;
  entryPrice: number;
  currentPrice: number;
  stopLoss: number;
  quantity: number;
  investment: number;
  pnlPercent: number;
  pnlUsd: number;
  entryTime: string;
  status: 'Open' | 'Pending';
  takeProfitStages?: {
    stage: number;
    price: number;
    profit: number;
    size: number;
    triggered: boolean;
  }[];
  trailingExitStages?: {
    stage: number;
    activatesAt: number;
    trail: number;
    size: number;
    active: boolean;
  }[];
};

type Bot = {
  id: string;
  name: string;
  strategy: string;
  tradingPairs: string[];
  positions: Position[];
};

const LiveTradesPage: React.FC = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<'live' | 'paper'>('live');
  const [bots, setBots] = useState<Bot[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedPositions, setExpandedPositions] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState('');

  const fetchActiveBots = async (runMode: 'live' | 'paper') => {
    setLoading(true);
    setError(null);

    try {
      // Fetch active bots based on selected tab
      const { data: botsData, error: botsError } = await supabase
        .from('bots')
        .select('*')
        .eq('user_id', user.id)
        .eq('is_active', true)
        .eq('run_mode', runMode);

      if (botsError) throw botsError;

      if (!botsData || botsData.length === 0) {
        setBots([]);
        setLoading(false);
        return;
      }

      // TODO: Fetch live/paper positions from bot_activity_log or external API
      // For now, show message that positions are not available yet
      setBots([]);
      setError(
        runMode === 'live'
          ? 'Live positions tracking is coming soon. This feature will display real-time trading positions from your live bots.'
          : 'Paper trading positions tracking is coming soon. This feature will display positions from your practice bots.'
      );

    } catch (err: any) {
      console.error('Error fetching active bots:', err);
      setError(err.message || `Failed to load ${runMode} trades`);
      toast.error(`Failed to load ${runMode} trades`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.id) {
      fetchActiveBots(activeTab);
    }
  }, [user?.id, activeTab]);

  const togglePositionExpand = (positionId: string) => {
    const newExpanded = new Set(expandedPositions);
    if (newExpanded.has(positionId)) {
      newExpanded.delete(positionId);
    } else {
      newExpanded.add(positionId);
    }
    setExpandedPositions(newExpanded);
  };

  const handleClosePosition = async (botId: string, positionId: string) => {
    if (!window.confirm('Are you sure you want to close this position?')) {
      return;
    }

    // TODO: Implement close position logic
    toast.info('Close position feature coming soon');
  };

  const handleCloseAllForPair = async (botId: string, pair: string) => {
    if (!window.confirm(`Are you sure you want to close all positions for ${pair}?`)) {
      return;
    }

    // TODO: Implement close all positions for pair logic
    toast.info('Close all positions feature coming soon');
  };

  const getTotalActivePositions = () => {
    return bots.reduce((sum, bot) => sum + bot.positions.length, 0);
  };

  return (
    <DashboardLayout>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
        className="space-y-6"
      >
              {/* Header */}
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                  <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Trading Positions</h1>
                  <p className="text-gray-600 dark:text-gray-400 mt-1">Monitor and manage your active trading positions</p>
                </div>

                <div className="flex flex-col md:flex-row gap-3 w-full md:w-auto">
                  <div className="relative flex-1 md:flex-initial">
                    <input
                      type="text"
                      placeholder="Search trades..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full md:w-64 pl-10 pr-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                  </div>

                  <div className="relative">
                    <select className="w-full md:w-40 pl-3 pr-10 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none">
                      <option value="all">All Status</option>
                      <option value="open">Open</option>
                      <option value="pending">Pending</option>
                    </select>
                    <ChevronDown className="absolute right-3 top-2.5 h-5 w-5 text-gray-400 pointer-events-none" />
                  </div>
                </div>
              </div>

              {/* Tabs */}
              <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                <div className="flex border-b border-gray-200 dark:border-gray-700">
                  <button
                    onClick={() => setActiveTab('live')}
                    className={`flex-1 px-6 py-4 font-semibold transition-all relative ${
                      activeTab === 'live'
                        ? 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20'
                        : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                    }`}
                  >
                    <div className="flex items-center justify-center gap-2">
                      <span className="text-xl">🔴</span>
                      <span>Live Trades</span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                        activeTab === 'live'
                          ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                      }`}>
                        {getTotalActivePositions()}
                      </span>
                    </div>
                    {activeTab === 'live' && (
                      <div className="absolute bottom-0 left-0 right-0 h-1 bg-blue-600 dark:bg-blue-400" />
                    )}
                  </button>

                  <button
                    onClick={() => setActiveTab('paper')}
                    className={`flex-1 px-6 py-4 font-semibold transition-all relative ${
                      activeTab === 'paper'
                        ? 'text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20'
                        : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                    }`}
                  >
                    <div className="flex items-center justify-center gap-2">
                      <span className="text-xl">📝</span>
                      <span>Paper Trades</span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                        activeTab === 'paper'
                          ? 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                      }`}>
                        {getTotalActivePositions()}
                      </span>
                    </div>
                    {activeTab === 'paper' && (
                      <div className="absolute bottom-0 left-0 right-0 h-1 bg-green-600 dark:bg-green-400" />
                    )}
                  </button>
                </div>
              </div>

              {/* Loading State */}
              {loading ? (
                <div className="flex items-center justify-center h-64">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
                </div>
              ) : error ? (
                <div className={`p-8 rounded-lg border ${
                  activeTab === 'live'
                    ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'
                    : 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                }`}>
                  <div className="flex items-start gap-4">
                    <AlertTriangle className={`h-6 w-6 flex-shrink-0 mt-1 ${
                      activeTab === 'live'
                        ? 'text-blue-600 dark:text-blue-400'
                        : 'text-green-600 dark:text-green-400'
                    }`} />
                    <div>
                      <h3 className={`text-lg font-semibold mb-2 ${
                        activeTab === 'live'
                          ? 'text-blue-900 dark:text-blue-100'
                          : 'text-green-900 dark:text-green-100'
                      }`}>
                        {activeTab === 'live' ? 'Live Trading' : 'Paper Trading'} Coming Soon
                      </h3>
                      <p className={activeTab === 'live'
                        ? 'text-blue-800 dark:text-blue-200'
                        : 'text-green-800 dark:text-green-200'
                      }>
                        {error}
                      </p>
                      <p className={`mt-3 ${
                        activeTab === 'live'
                          ? 'text-blue-700 dark:text-blue-300'
                          : 'text-green-700 dark:text-green-300'
                      }`}>
                        Currently, you can test trading strategies using the <strong>Backtests</strong> feature. {activeTab === 'live' ? 'Live' : 'Paper'} trading positions will be displayed here once your bots start executing {activeTab === 'live' ? 'real' : 'practice'} trades.
                      </p>
                    </div>
                  </div>
                </div>
              ) : bots.length === 0 ? (
                <div className="bg-white dark:bg-gray-800 rounded-xl p-12 text-center shadow-md border border-gray-200 dark:border-gray-700">
                  <AlertTriangle className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-xl font-medium text-gray-900 dark:text-white mb-2">
                    No Active {activeTab === 'live' ? 'Live' : 'Paper'} Bots
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 mb-6">
                    You don't have any active {activeTab === 'live' ? 'live' : 'paper'} trading bots. Activate a bot to start {activeTab === 'live' ? 'live' : 'paper'} trading.
                  </p>
                  <a
                    href={activeTab === 'live' ? '/active-bots' : '/my-bots'}
                    className={`inline-block px-6 py-3 rounded-lg text-white transition-colors ${
                      activeTab === 'live'
                        ? 'bg-blue-600 hover:bg-blue-700'
                        : 'bg-green-600 hover:bg-green-700'
                    }`}
                  >
                    Go to {activeTab === 'live' ? 'Active' : 'My'} Bots
                  </a>
                </div>
              ) : (
                /* Bot Cards with Positions */
                <div className="space-y-8">
                  {bots.map((bot) => (
                    <div key={bot.id} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                      {/* Bot Header */}
                      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                          <div>
                            <h3 className="text-xl font-bold text-gray-900 dark:text-white">{bot.name}</h3>
                            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{bot.strategy}</p>
                          </div>

                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm text-gray-600 dark:text-gray-400">Trading pairs:</span>
                            {bot.tradingPairs.map((pair) => (
                              <span
                                key={pair}
                                className="px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full text-sm font-medium"
                              >
                                {pair}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>

                      {/* Positions grouped by pair */}
                      {bot.tradingPairs.map((pair) => {
                        const pairPositions = bot.positions.filter(p => p.symbol === pair);
                        if (pairPositions.length === 0) return null;

                        return (
                          <div key={pair} className="border-b border-gray-200 dark:border-gray-700 last:border-b-0">
                            {/* Pair Header */}
                            <div className="px-6 py-3 bg-gray-50 dark:bg-gray-900/50 flex justify-between items-center">
                              <h4 className="text-base font-semibold text-gray-900 dark:text-white">
                                {pair} <span className="text-sm text-gray-500 dark:text-gray-400">({pairPositions.length} active trade{pairPositions.length !== 1 ? 's' : ''})</span>
                              </h4>
                              <button
                                onClick={() => handleCloseAllForPair(bot.id, pair)}
                                className="text-sm text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 font-medium"
                              >
                                Close All for {pair}
                              </button>
                            </div>

                            {/* Positions Table */}
                            <div className="overflow-x-auto">
                              <table className="w-full">
                                <thead className="bg-gray-50 dark:bg-gray-900/50">
                                  <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Type</th>
                                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Entry Price</th>
                                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Current Price</th>
                                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Stop Loss</th>
                                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Quantity</th>
                                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Investment</th>
                                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">P&L %</th>
                                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">P&L $</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Entry Time</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
                                  </tr>
                                </thead>
                                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                  {pairPositions.map((position) => {
                                    const isExpanded = expandedPositions.has(position.id);

                                    return (
                                      <React.Fragment key={position.id}>
                                        <tr className="hover:bg-gray-50 dark:hover:bg-gray-900/30 transition-colors">
                                          <td className="px-6 py-4 whitespace-nowrap">
                                            <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${
                                              position.type === 'LONG'
                                                ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                                                : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                                            }`}>
                                              {position.type}
                                            </span>
                                          </td>
                                          <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900 dark:text-white font-medium">
                                            ${position.entryPrice.toFixed(2)}
                                          </td>
                                          <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900 dark:text-white font-medium">
                                            ${position.currentPrice.toFixed(2)}
                                          </td>
                                          <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900 dark:text-white">
                                            ${position.stopLoss.toFixed(2)}
                                          </td>
                                          <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900 dark:text-white">
                                            {position.quantity.toFixed(4)}
                                          </td>
                                          <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900 dark:text-white font-medium">
                                            ${position.investment.toFixed(2)}
                                          </td>
                                          <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                                            <span className={`font-bold ${position.pnlPercent >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                                              {position.pnlPercent >= 0 ? '+' : ''}{position.pnlPercent.toFixed(2)}%
                                            </span>
                                          </td>
                                          <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                                            <span className={`font-bold ${position.pnlUsd >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                                              {position.pnlUsd >= 0 ? '+' : ''}${position.pnlUsd.toFixed(2)}
                                            </span>
                                          </td>
                                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                                            {new Date(position.entryTime).toLocaleString('en-US', {
                                              month: 'numeric',
                                              day: 'numeric',
                                              year: 'numeric',
                                              hour: '2-digit',
                                              minute: '2-digit',
                                            })}
                                          </td>
                                          <td className="px-6 py-4 whitespace-nowrap">
                                            <span className="inline-flex px-3 py-1 text-xs font-semibold rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">
                                              {position.status}
                                            </span>
                                          </td>
                                          <td className="px-6 py-4 whitespace-nowrap text-center">
                                            <div className="flex items-center justify-center gap-2">
                                              <button
                                                onClick={() => togglePositionExpand(position.id)}
                                                className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
                                              >
                                                {isExpanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                                              </button>
                                              <button
                                                onClick={() => handleClosePosition(bot.id, position.id)}
                                                className="text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300"
                                              >
                                                <X className="h-5 w-5" />
                                              </button>
                                            </div>
                                          </td>
                                        </tr>

                                        {/* Expanded Details */}
                                        {isExpanded && (
                                          <tr>
                                            <td colSpan={11} className="px-6 py-6 bg-gray-50 dark:bg-gray-900/50">
                                              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                                {/* Take Profit Stages */}
                                                {position.takeProfitStages && position.takeProfitStages.length > 0 && (
                                                  <div>
                                                    <h5 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Take Profit Stages</h5>
                                                    <div className="space-y-3">
                                                      {position.takeProfitStages.map((stage) => (
                                                        <div key={stage.stage} className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                                                          <div className="flex items-center justify-between mb-2">
                                                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                                              Stage {stage.stage} {stage.triggered ? '– Triggered' : '– Pending'}
                                                            </span>
                                                            {stage.triggered && (
                                                              <span className="text-xs text-green-600 dark:text-green-400 font-semibold">✓</span>
                                                            )}
                                                          </div>
                                                          <div className="space-y-1 text-xs text-gray-600 dark:text-gray-400">
                                                            <p><span className="font-medium">Price:</span> ${stage.price.toFixed(2)}</p>
                                                            <p><span className="font-medium">Profit:</span> {stage.profit.toFixed(2)}% of position</p>
                                                            <p><span className="font-medium">Size:</span> {stage.size}% of position</p>
                                                          </div>
                                                        </div>
                                                      ))}
                                                    </div>
                                                  </div>
                                                )}

                                                {/* Trailing Exit Stages */}
                                                {position.trailingExitStages && position.trailingExitStages.length > 0 && (
                                                  <div>
                                                    <h5 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Trailing Exit Stages</h5>
                                                    <div className="space-y-3">
                                                      {position.trailingExitStages.map((stage) => (
                                                        <div key={stage.stage} className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                                                          <div className="flex items-center justify-between mb-2">
                                                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                                              Stage {stage.stage} {stage.active ? '– Active' : '– Pending'}
                                                            </span>
                                                            {stage.active && (
                                                              <span className="text-xs text-blue-600 dark:text-blue-400 font-semibold">⏱</span>
                                                            )}
                                                          </div>
                                                          <div className="space-y-1 text-xs text-gray-600 dark:text-gray-400">
                                                            <p><span className="font-medium">Activates at:</span> ${stage.activatesAt.toFixed(2)}</p>
                                                            <p><span className="font-medium">Trail:</span> {stage.trail.toFixed(2)}%</p>
                                                            <p><span className="font-medium">Size:</span> {stage.size}% of position</p>
                                                          </div>
                                                        </div>
                                                      ))}
                                                    </div>
                                                  </div>
                                                )}
                                              </div>

                                              {/* Action Buttons */}
                                              <div className="mt-6 flex gap-3">
                                                <button className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm">
                                                  Close at Market Price
                                                </button>
                                                <button
                                                  onClick={() => handleCloseAllForPair(bot.id, pair)}
                                                  className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium text-sm"
                                                >
                                                  Close All for {pair}
                                                </button>
                                              </div>
                                            </td>
                                          </tr>
                                        )}
                                      </React.Fragment>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              )}
      </motion.div>
    </DashboardLayout>
  );
};

export default LiveTradesPage;
